import * as PIXI from "pixi.js";
import { Settings } from "../../settings";
import { formatToCustomLocale } from "../../utilities";
import { createCoinLabel, setCoinLabel, warmCoinLabels } from "../HoldAndWin/coinLabel";
import { cosmeticCoinValues } from "./coinValues";

/**
 * The coin's number on a landed `c` spine — its `txt_slot_coin` slot, exactly
 * as the Hold & Win cells wear it (HoldAndWinOverlayCell): the coinLabel
 * sprite at the rig's own size, so the spine's scale and the slot's bone carry
 * it, landing and pulsing with the coin. Never rounded.
 *
 * Shared by the base and the free-spins overlay reels.
 */

export const COIN_VALUE_SLOT = "txt_slot_coin";

// The size the spine wears it at (rig units — the spine scales it).
const SPINE_FONT_SIZE = Settings.SYMBOL_HEIGHT * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;

// ── The widest a coin number may be before it is squeezed, x the CELL HEIGHT ──
// ONE ratio for the spine's number and the texture's (Vladimir, 2026-09-30:
// "coins textures and coins spines ... must be identical"). Both are drawn at
// COIN_VALUE_FONT_SIZE_MULTIPLIER x the cell height, so the same ratio squeezes
// a value by exactly the same amount on both. The textures used 0.76 x the cell
// WIDTH (0.9 x width in Hold & Win) and squeezed values the spine did not.
export const COIN_LABEL_MAX_WIDTH_RATIO = 0.9;

// ── Formatted strings, cached ──
// formatToCustomLocale reads the store and runs toLocaleString on every call;
// a coin wrapping on the strip would pay that each time. The same value always
// formats the same way for one currency and locale, so each is formatted once.
const formatted = new Map();
const formatKey = () => {
  let currency = "";
  try {
    // eslint-disable-next-line global-require
    const cfg = require("../../State/Store/store").default.getState()?.game?.currencyConfig;
    currency = cfg ? `${cfg.subunits}|${cfg.exponent}` : "";
  } catch {
    // Before the store exists — the defaults formatToCustomLocale uses.
  }
  return `${window.__OPTIONS__?.locale || ""}|${currency}`;
};

/** `value` as the coin prints it — formatToCustomLocale, cached. */
export const formatCoinValue = (value) => {
  const key = `${formatKey()}|${value}`;
  let label = formatted.get(key);
  if (label === undefined) {
    if (formatted.size > 2000) formatted.clear(); // bets and currencies are few
    label = formatToCustomLocale(value);
    formatted.set(key, label);
  }
  return label;
};

/**
 * Pre-draw every value a passing coin can show — the seven cosmetic values for
 * the current bet — at the reel's size (`reelFontSize`, px) and the spine's.
 * Called when a reel is built, when the bet changes and on a resize, so no
 * label is ever rasterised mid-spin.
 */
export const warmCoinValueLabels = (reelFontSize) => {
  const labels = cosmeticCoinValues().map(formatCoinValue);
  warmCoinLabels(labels, [reelFontSize, SPINE_FONT_SIZE]);
};

/**
 * Put `value` on `spine`'s coin slot.
 *
 * @returns {{label: PIXI.Sprite, slotName: string}|null} null when the rig has
 *          no coin slot or there is no value — a missing slot costs the
 *          number, never the round.
 */
export const wearCoinValue = (spine, value) => {
  if (!spine?.skeleton?.findSlot?.(COIN_VALUE_SLOT) || !(value > 0)) return null;
  const label = createCoinLabel();
  label.name = "coin_value_text";
  // setCoinLabel(label, formatCoinValue(value), SPINE_FONT_SIZE, Settings.SYMBOL_HEIGHT * 0.9);
  setCoinLabel(
    label,
    formatCoinValue(value),
    SPINE_FONT_SIZE,
    Settings.SYMBOL_HEIGHT * COIN_LABEL_MAX_WIDTH_RATIO,
  );
  // spine.addSlotObject(COIN_VALUE_SLOT, label);
  // return { label, slotName: COIN_VALUE_SLOT };
  // In a holder: spine-pixi rewrites a slot object's transform every frame,
  // which wiped setCoinLabel's horizontal squeeze — wide values overflowed
  // the coin while the reel texture squeezed them (Vladimir, 2026-10-13).
  const holder = new PIXI.Container();
  holder.name = "coin_value_holder";
  holder.addChild(label);
  spine.addSlotObject(COIN_VALUE_SLOT, holder);
  return { label: holder, slotName: COIN_VALUE_SLOT };
};
